// frontend/src/components/auth/ProfileSettings.js
import React, { useState, useEffect } from 'react';
import {
  User,
  Building2,
  UserCheck,
  AlertCircle,
  CheckCircle,
  Loader,
  MapPin,
  Globe,
  Mail,
  Phone,
  Send
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from './AuthProvider';
import { PageHeader } from '../common';
import { PasswordStrengthIndicator } from './AuthUtils';
import { LocationSelector } from '../LocationSelector'; // Import the LocationSelector
import { api, authAPI } from '../../services/api';

export const ProfileSettings = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [locationData, setLocationData] = useState({
    country: user?.country || '',
    state_province: user?.state_province || '',
    city: user?.city || '',
    postal_code: user?.postal_code || '',
    latitude: user?.latitude || null,
    longitude: user?.longitude || null
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Notification settings state
  // CROW's two optional emails. Everything else it sends (verification,
  // password resets, receipts, trial notices) is transactional. The ~20
  // switches that were here described mail CROW never sends.
  const [notificationSettings, setNotificationSettings] = useState({
    maintenance_reminders: true,
    weekly_health_report: true,
    corvin_tips: true,
  });
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Email verification state
  const [emailVerificationStatus, setEmailVerificationStatus] = useState({
    verified: false,
    verified_at: null
  });
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);

  // Phone verification state
  const [phoneVerificationStatus, setPhoneVerificationStatus] = useState({
    verified: false,
    verified_at: null
  });
  const [phoneVerificationLoading, setPhoneVerificationLoading] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [showPhoneVerificationInput, setShowPhoneVerificationInput] = useState(false);

  const userTypes = [
    { value: 'mechanic', label: 'Automotive Mechanic', icon: UserCheck },
    { value: 'dealer', label: 'Parts Dealer', icon: Building2 },
    { value: 'consumer', label: 'Car Owner', icon: User }
  ];

  // Load user's current location, verification status, and notification settings on mount
  useEffect(() => {
    loadUserLocation();
    loadVerificationStatus();
    loadNotificationSettings();
  }, []);

  const loadUserLocation = async () => {
    try {
      const response = await api.get('/location/user-location');
      if (response.data) {
        setLocationData({
          country: response.data.country || '',
          state_province: response.data.state_province || '',
          city: response.data.city || '',
          postal_code: response.data.postal_code || '',
          latitude: response.data.latitude || null,
          longitude: response.data.longitude || null
        });
      }
    } catch (error) {
      console.error('Failed to load user location:', error);
    }
  };

  const loadVerificationStatus = async () => {
    try {
      // Load email verification status
      const emailStatus = await api.auth.getEmailVerificationStatus();
      // Map backend field names to frontend state
      setEmailVerificationStatus({
        verified: emailStatus.email_verified || false,
        verified_at: emailStatus.email_verified_at || null
      });

      // Load phone verification status (when implemented)
      try {
        const phoneStatus = await api.auth.getPhoneVerificationStatus();
        // Map backend field names to frontend state
        setPhoneVerificationStatus({
          verified: phoneStatus.phone_verified || false,
          verified_at: phoneStatus.phone_verified_at || null
        });
      } catch (error) {
        // Phone verification not implemented yet
        console.log('Phone verification not available yet');
      }
    } catch (error) {
      console.error('Failed to load verification status:', error);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await api.get('/notifications/settings');
      if (response.data) {
        setNotificationSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      // Use default settings if API fails
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const updatedUser = await authAPI.updateProfile(formData);
      updateUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Notification settings handlers


  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setNotificationLoading(true);
    
    try {
      await api.put('/notifications/settings', notificationSettings);
      toast.success('Notification settings updated successfully');
    } catch (error) {
      toast.error('Failed to update notification settings');
      console.error('Notification settings error:', error);
    } finally {
      setNotificationLoading(false);
    }
  };



  // Email verification functions
  const handleResendEmailVerification = async () => {
    setEmailVerificationLoading(true);
    try {
      await api.auth.resendEmailVerification(formData.email);
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  // Phone verification functions
  const handleSendPhoneVerification = async () => {
    if (!formData.phone) {
      toast.error('Please enter a phone number first');
      return;
    }

    setPhoneVerificationLoading(true);
    try {
      await api.auth.sendPhoneVerification(formData.phone);
      setShowPhoneVerificationInput(true);
      toast.success('Verification code sent to your phone!');
    } catch (error) {
      toast.error('Failed to send verification code');
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneVerificationCode) {
      toast.error('Please enter the verification code');
      return;
    }

    setPhoneVerificationLoading(true);
    try {
      await api.auth.verifyPhone(formData.phone, phoneVerificationCode);
      setPhoneVerificationStatus({ verified: true, verified_at: new Date().toISOString() });
      setShowPhoneVerificationInput(false);
      setPhoneVerificationCode('');
      toast.success('Phone number verified successfully!');
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handleLocationChange = async (location) => {
    if (!location) {
      setLocationData({
        country: '',
        state_province: '',
        city: '',
        postal_code: '',
        latitude: null,
        longitude: null
      });
      return;
    }

    setLocationLoading(true);
    try {
      // Update location in backend
      await api.post('/location/update-geographic-location', {
        country: location.country,
        state_province: location.state_province,
        city: location.city,
        postal_code: location.postal_code,
        latitude: location.latitude,
        longitude: location.longitude
      });

      // Update local state
      setLocationData(location);
      
      // Update user context if needed
      updateUser({
        ...user,
        country: location.country,
        city: location.city,
        state_province: location.state_province
      });

      toast.success('Location updated successfully');
    } catch (error) {
      toast.error('Failed to update location');
      console.error('Location update error:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setErrors({});
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Format initial location for the selector
  const initialLocation = locationData.city ? {
    country: locationData.country,
    state_province: locationData.state_province,
    city: locationData.city,
    postal_code: locationData.postal_code,
    latitude: locationData.latitude,
    longitude: locationData.longitude
  } : null;

  // Notification type configurations


  return (
    <div className="space-y-6">
      <PageHeader title="Profile & Settings" />
      <div className="bg-surface-card rounded-card border border-border-default shadow-elevation-1">

        {/* Tabs */}
        <div className="border-b border-border-default">
          <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto scrollbar-hide">
            {[
              { id: 'profile', label: 'Profile' },
              { id: 'security', label: 'Security' },
              { id: 'notifications', label: 'Notifications' },
              { id: 'subscription', label: 'Subscription' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-border-default'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-ink-soft">
                  Full Name
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent focus:border-accent"
                />
              </div>

              {/* Email Field with Verification */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink-soft">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full border border-border-strong rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-accent focus:border-accent"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {emailVerificationStatus.verified ? (
                      <CheckCircle className="w-5 h-5 text-success" />
                    ) : (
                      <Mail className="w-5 h-5 text-ink-muted" />
                    )}
                  </div>
                </div>
                
                {/* Email Verification Status */}
                {emailVerificationStatus.verified ? (
                  <div className="mt-2 flex items-center text-sm text-success">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Email verified
                    {emailVerificationStatus.verified_at && (
                      <span className="ml-2 text-ink-muted">
                        on {new Date(emailVerificationStatus.verified_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center text-sm text-warning">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Email not verified
                    </div>
                    <button
                      type="button"
                      onClick={handleResendEmailVerification}
                      disabled={emailVerificationLoading}
                      className="text-sm text-accent hover:text-accent-hover disabled:opacity-50 flex items-center"
                    >
                      {emailVerificationLoading ? (
                        <Loader className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Send className="w-4 h-4 mr-1" />
                      )}
                      {emailVerificationLoading ? 'Sending...' : 'Resend verification'}
                    </button>
                  </div>
                )}
              </div>

              {/* Phone Field with Verification */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-ink-soft">
                  Phone Number
                </label>
                <div className="mt-1 relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="block w-full border border-border-strong rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-accent focus:border-accent"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    {phoneVerificationStatus.verified ? (
                      <CheckCircle className="w-5 h-5 text-success" />
                    ) : (
                      <Phone className="w-5 h-5 text-ink-muted" />
                    )}
                  </div>
                </div>
                
                {/* Phone Verification Status */}
                {phoneVerificationStatus.verified ? (
                  <div className="mt-2 flex items-center text-sm text-success">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Phone verified
                    {phoneVerificationStatus.verified_at && (
                      <span className="ml-2 text-ink-muted">
                        on {new Date(phoneVerificationStatus.verified_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-warning">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Phone not verified
                      </div>
                      <button
                        type="button"
                        onClick={handleSendPhoneVerification}
                        disabled={phoneVerificationLoading || !formData.phone}
                        className="text-sm text-accent hover:text-accent-hover disabled:opacity-50 flex items-center"
                      >
                        {phoneVerificationLoading ? (
                          <Loader className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Send className="w-4 h-4 mr-1" />
                        )}
                        {phoneVerificationLoading ? 'Sending...' : 'Send verification code'}
                      </button>
                    </div>
                    
                    {/* Phone Verification Code Input */}
                    {showPhoneVerificationInput && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={phoneVerificationCode}
                          onChange={(e) => setPhoneVerificationCode(e.target.value)}
                          placeholder="Enter 6-digit code"
                          className="flex-1 border border-border-strong rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-accent focus:border-accent"
                          maxLength={6}
                        />
                        <button
                          type="button"
                          onClick={handleVerifyPhone}
                          disabled={phoneVerificationLoading || !phoneVerificationCode}
                          className="px-3 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent-hover disabled:bg-border-default disabled:cursor-not-allowed"
                        >
                          {phoneVerificationLoading ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            'Verify'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-soft mb-3">
                  User Type
                </label>
                <div className="bg-surface-page rounded-lg p-4">
                  <div className="flex items-center">
                    {(() => {
                      const currentType = userTypes.find(type => type.value === user?.user_type);
                      const Icon = currentType?.icon || User;
                      return (
                        <>
                          <Icon className="w-5 h-5 text-accent mr-3" />
                          <span className="font-medium text-ink-soft">{currentType?.label || 'Unknown'}</span>
                          <span className="ml-2 text-sm text-ink-muted">(Cannot be changed)</span>
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-sm text-ink-muted mt-2">
                    Your user type determines your subscription pricing and available features. 
                    This cannot be changed after registration.
                  </p>
                </div>
              </div>

              {/* Location Section - NEW */}
              <div className="border-t border-border-default pt-6">
                <div className="flex items-center mb-4">
                  <MapPin className="w-5 h-5 text-ink-muted mr-2" />
                  <h3 className="text-sm font-medium text-ink-soft">Location Settings</h3>
                </div>
                
                <div className="bg-surface-page rounded-lg p-4">
                  <p className="text-sm text-ink-muted mb-4">
                    Your location helps us provide localized pricing, currency, and measurement units.
                  </p>
                  
                  <LocationSelector
                    initialLocation={initialLocation}
                    onLocationChange={handleLocationChange}
                    placeholder="Search for your city..."
                    className="mb-4"
                  />

                  {locationLoading && (
                    <div className="flex items-center text-sm text-ink-muted">
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Updating location settings...
                    </div>
                  )}

                  {/* Display current location info */}
                  {locationData.city && !locationLoading && (
                    <div className="mt-4 p-3 bg-surface-card rounded-card border border-border-default">
                      <div className="flex items-start">
                        <Globe className="w-4 h-4 text-ink-muted mt-0.5 mr-2" />
                        <div className="text-sm">
                          <div className="font-medium text-ink-soft">Current Location</div>
                          <div className="text-ink-muted mt-1">
                            {locationData.city}, {locationData.state_province}
                            <br />
                            {locationData.country}
                            {locationData.postal_code && (
                              <span className="text-ink-muted"> • {locationData.postal_code}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-accent text-white px-4 py-2 rounded-md hover:bg-accent-hover disabled:bg-border-default disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Save Changes
                </button>
              </div>
            </form>
          )}

          {activeTab === 'security' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-ink-soft mb-4">Change Password</h3>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="current_password" className="block text-sm font-medium text-ink-soft">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="current_password"
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      className="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent focus:border-accent"
                    />
                  </div>

                  <div>
                    <label htmlFor="new_password" className="block text-sm font-medium text-ink-soft">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="new_password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      className="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent focus:border-accent"
                    />
                    <p className="mt-1 text-sm text-ink-muted">
                      Password must be at least 8 characters with letters and numbers
                    </p>
                    <PasswordStrengthIndicator password={passwordData.new_password} />
                  </div>

                  <div>
                    <label htmlFor="confirm_password" className="block text-sm font-medium text-ink-soft">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirm_password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-accent focus:border-accent ${
                        errors.confirm_password ? 'border-danger' : 'border-border-strong'
                      }`}
                    />
                    {errors.confirm_password && (
                      <p className="mt-1 text-sm text-danger flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.confirm_password}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="bg-accent text-white px-4 py-2 rounded-md hover:bg-accent-hover disabled:bg-border-default disabled:cursor-not-allowed flex items-center"
                >
                  {passwordLoading ? (
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Change Password
                </button>
              </div>
            </form>
          )}

          {activeTab === 'notifications' && (
            <form onSubmit={handleNotificationSubmit} className="space-y-6 max-w-2xl">
              <div>
                <h3 className="text-section-title font-display text-ink">Email notifications</h3>
                <p className="text-body text-ink-muted mt-1">
                  Choose which optional emails CROW sends you. Account, security, and billing emails
                  (verification, password resets, receipts) always send.
                </p>
              </div>

              <div className="rounded-card border border-border-default divide-y divide-border-default">
                  <label className="flex items-start justify-between gap-4 p-4 cursor-pointer">
                    <span>
                      <span className="block text-body-strong text-ink">Maintenance reminders</span>
                      <span className="block text-small text-ink-muted mt-0.5">When a service on one of your vehicles is due within a week, or is overdue.</span>
                    </span>
                    <span className="relative inline-flex items-center flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={notificationSettings.maintenance_reminders}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, maintenance_reminders: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <span aria-hidden="true" className="w-11 h-6 bg-border-strong rounded-full peer peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2 peer-checked:bg-accent after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:duration-state peer-checked:after:translate-x-full"></span>
                    </span>
                  </label>
                  <label className="flex items-start justify-between gap-4 p-4 cursor-pointer">
                    <span>
                      <span className="block text-body-strong text-ink">Weekly health report</span>
                      <span className="block text-small text-ink-muted mt-0.5">A weekly summary of each vehicle's condition and upcoming maintenance.</span>
                    </span>
                    <span className="relative inline-flex items-center flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={notificationSettings.weekly_health_report}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, weekly_health_report: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <span aria-hidden="true" className="w-11 h-6 bg-border-strong rounded-full peer peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2 peer-checked:bg-accent after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:duration-state peer-checked:after:translate-x-full"></span>
                    </span>
                  </label>
                  <label className="flex items-start justify-between gap-4 p-4 cursor-pointer">
                    <span>
                      <span className="block text-body-strong text-ink">Corvin's tips</span>
                      <span className="block text-small text-ink-muted mt-0.5">Short notes from Corvin about what needs attention. One at a time, in the app, never by email.</span>
                    </span>
                    <span className="relative inline-flex items-center flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={notificationSettings.corvin_tips}
                        onChange={(e) => setNotificationSettings(prev => ({ ...prev, corvin_tips: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <span aria-hidden="true" className="w-11 h-6 bg-border-strong rounded-full peer peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2 peer-checked:bg-accent after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:duration-state peer-checked:after:translate-x-full"></span>
                    </span>
                  </label>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={notificationLoading}
                  className="inline-flex items-center gap-2 min-h-touch px-4 rounded-control bg-accent text-white font-display font-semibold hover:bg-accent-hover transition-colors duration-state disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  {notificationLoading ? <Loader className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
                  Save notification settings
                </button>
              </div>
            </form>
          )}

          {activeTab === 'subscription' && (
            <div className="text-center py-8">
              <h3 className="text-lg font-medium text-ink-soft mb-2">Subscription Management</h3>
              <p className="text-ink-muted mb-4">
                Current Plan: <span className="font-semibold capitalize">
                  {['essential', 'mechanic_essential'].includes(user?.subscription_tier) ? 'Mechanic Pro' :
                   ['pro', 'mechanic_pro'].includes(user?.subscription_tier) ? 'Mechanic Premium' :
                   user?.subscription_tier === 'basic' ? 'Basic' :
                   user?.subscription_tier === 'professional' ? 'Professional' :
                   user?.subscription_tier || 'Free'}
                </span>
              </p>
              <button
                onClick={() => window.location.href = '/billing'}
                className="bg-accent text-white px-6 py-2 rounded-md hover:bg-accent-hover"
              >
                Manage Subscription
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};